import React from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import usePageMeta from "@/lib/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  return String(detail);
};

export default function ForgotPassword() {
  const [email, setEmail] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [sent, setSent] = React.useState(false);

  usePageMeta({
    title: "Reset your password | CV AI PRO",
    description: "Request a password reset link.",
    path: "/forgot-password",
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: email.trim() });
      setSent(true);
    } catch (err) {
      toast.error("Couldn't process that", {
        description: formatError(err?.response?.data?.detail),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-black text-white" data-testid="forgot-password-page">
      <div aria-hidden className="absolute -top-32 left-1/2 -translate-x-1/2 h-[520px] w-[520px] rounded-full bg-blue-600/15 blur-[140px]" />
      <div aria-hidden className="absolute inset-0 grid-bg" />

      <div className="relative mx-auto max-w-md px-4 sm:px-6 pt-32 sm:pt-40 pb-24">
        {sent ? (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="text-center" data-testid="forgot-sent">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30">
              <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            </div>
            <h1 className="mt-8 text-3xl sm:text-4xl font-semibold tracking-tighter text-white text-balance">
              Check your inbox.
            </h1>
            <p className="mt-5 text-zinc-400 text-balance">
              If an account exists for <span className="text-white">{email}</span>, a reset link
              is on its way. It works once and expires in 1 hour.
            </p>
            <Link
              to="/login"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-white text-black px-6 py-3 text-sm font-medium hover:bg-zinc-200 transition-colors"
              data-testid="forgot-back-login"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to login
            </Link>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-white/5 border border-white/10">
              <KeyRound className="h-6 w-6 text-zinc-300" />
            </div>
            <h1 className="mt-8 text-3xl sm:text-4xl font-semibold tracking-tighter text-white text-balance" data-testid="forgot-headline">
              Forgot your password?
            </h1>
            <p className="mt-4 text-zinc-400">
              Enter your account email and we'll send you a reset link.
            </p>
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <Label htmlFor="reset-email" className="text-xs text-zinc-400">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  required
                  className="mt-1.5 bg-black/40 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-white/30"
                  data-testid="forgot-input-email"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-white text-black px-6 py-3.5 text-sm font-medium hover:bg-zinc-200 transition-colors disabled:opacity-60"
                data-testid="forgot-submit"
              >
                {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                Send reset link
              </button>
            </form>
            <p className="mt-6 text-center text-xs text-zinc-500">
              Remembered it?{" "}
              <Link to="/login" className="text-zinc-300 hover:text-white underline underline-offset-2" data-testid="forgot-login-link">
                Sign in
              </Link>
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
}
